import { z } from 'zod';
import { ShiftSettingSchema, ShiftTimeBucketSchema } from '../rate/enums.schema.js';
import { MatchingStateSchema, RequestingEntityTypeSchema } from './enums.schema.js';
// Shift-request request/response shapes. The request shape is
// polymorphic across (FACILITY | HOMECARE_CLIENT) — `requestingEntityType`
// discriminates which entity the request is from; the service layer
// resolves the owning row + populates `facilityId` / `homecareClientId`
// / `recipientId` on the row. The response carries `requestingEntityType`
// for the consumer's display, but the canonical owner reference lives
// in `facility` or `homecareClient` (one is non-null).
const NonEmptyString = z.string().min(1);
export const CreateShiftRequestSchema = z
    .object({
    roleId: z.string().uuid(),
    setting: ShiftSettingSchema,
    scheduledStart: z.string().datetime(),
    scheduledEnd: z.string().datetime(),
    requestingEntityType: RequestingEntityTypeSchema,
    // FACILITY: facilityId required; HOMECARE_CLIENT: homecareClientId + recipientId required.
    facilityId: z.string().uuid().optional(),
    homecareClientId: z.string().uuid().optional(),
    recipientId: z.string().uuid().optional(),
    specialConstraints: NonEmptyString.max(2000).optional(),
    requiredSkills: z.array(z.string()).default([]),
    // Optional per-request matching overrides
    offerTierSize: z.number().int().min(1).max(20).optional(),
    offerExpirationMinutes: z.number().int().min(1).max(1440).optional(),
    expiresAt: z.string().datetime().optional(), // platform default applied if omitted
    // Phase 21 (3.1-1) — headcount. A UI multiplier: "2 PSW for this slot" posts
    // 2 independent, identically-specced requests. Facility only; homecare is
    // per-recipient (single). Defaults to 1.
    caregiversNeeded: z.number().int().min(1).max(20).optional(),
    // Phase 21 (3.1-8) — optional facility-preferred caregiver. Gets a targeted
    // priority offer with a 24h fallback to normal broadcast (non-exclusive).
    // Facility only. When set with caregiversNeeded > 1, only the FIRST posted
    // request carries the preference (the same caregiver can't take two slots).
    preferredCaregiverId: z.string().uuid().optional(),
})
    .superRefine((val, ctx) => {
    const start = new Date(val.scheduledStart).getTime();
    const end = new Date(val.scheduledEnd).getTime();
    if (Number.isFinite(start) && Number.isFinite(end) && end <= start) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'scheduledEnd must be strictly after scheduledStart',
            path: ['scheduledEnd'],
        });
    }
    // Home-care visits carry a 3-hour minimum (facility/LTC shifts have none).
    if (val.setting === 'HOMECARE' &&
        Number.isFinite(start) &&
        Number.isFinite(end) &&
        end > start &&
        end - start < 3 * 60 * 60 * 1000) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Home-care visits have a 3-hour minimum.',
            path: ['scheduledEnd'],
        });
    }
    // `facilityId` / `homecareClientId` are NOT required in the body: the only
    // consumers are the session-scoped `/me/{facility,homecare}/shift-requests`
    // endpoints, which DERIVE the owner from the caller's session and validate
    // ownership server-side (a body-supplied id is treated as defense-in-depth —
    // rejected only if it mismatches the session). Requiring it here silently
    // 400'd every buyer post from the portal (audit HIGH — the UI never sends it).
    // `recipientId` IS required for homecare: the caller chooses the recipient and
    // the service verifies it belongs to their account.
    if (val.requestingEntityType === 'HOMECARE_CLIENT' && !val.recipientId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'recipientId is required when requestingEntityType=HOMECARE_CLIENT',
            path: ['recipientId'],
        });
    }
});
export const ShiftRequestResponseSchema = z.object({
    id: z.string().uuid(),
    requestedByUserId: z.string().uuid(),
    requestingEntityType: RequestingEntityTypeSchema,
    facilityId: z.string().uuid().nullable(),
    homecareClientId: z.string().uuid().nullable(),
    recipientId: z.string().uuid().nullable(),
    roleId: z.string().uuid(),
    setting: ShiftSettingSchema,
    scheduledStart: z.string().datetime(),
    scheduledEnd: z.string().datetime(),
    shiftTimeBucket: ShiftTimeBucketSchema,
    weekendFlag: z.boolean(),
    statFlag: z.boolean(),
    lastMinFlag: z.boolean(),
    locationLat: z.string().nullable(),
    locationLng: z.string().nullable(),
    specialConstraints: z.string().nullable(),
    requiredSkills: z.array(z.string()),
    matchingState: MatchingStateSchema,
    offerTierSize: z.number().int(),
    offerExpirationMinutes: z.number().int().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    cancelledAt: z.string().datetime().nullable(),
    cancelReason: z.string().nullable(),
});
export const ShiftRequestListResponseSchema = z.object({
    items: z.array(ShiftRequestResponseSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
export const CancelShiftRequestSchema = z.object({
    reason: z.string().min(10).max(500),
});
//# sourceMappingURL=shift-request.schema.js.map